import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { StringUtils } from '@pk/powerkioskutils';

import { confirm } from 'devextreme/ui/dialog';
import moment from 'moment-timezone';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';

import { NotificationService } from 'src/app/notification/services/notification.service';
import { GraphqlService } from '../../graphql/graphql.service';
import { HelperService } from '../../shared/helper.service';
import { Product, RfqSession, RfqSessionBid, RfqSessionMarketSuggestion, RfqSessionProduct, RfqSessionSupplier, Supplier } from '../../shared/models';
import { AbstractPageForm } from '../../shared/pages/page-form.abstract';
import { productsValidator } from '../../shared/validators/products.validator';
import { AuctionSupplierOptOutModalComponent } from '../modals/auction-supplier-opt-out-modal/auction-supplier-opt-out-modal.component';
import { AuctionProduct, AuctionService } from '../services/auction.service';

@Component({
	selector: 'pk-broker-auction-in-progress',
	templateUrl: './auction-in-progress.component.html',
	styleUrls: ['./auction-in-progress.component.scss'],
	standalone: false,
})
export class AuctionInProgressComponent extends AbstractPageForm {
	@Input() rfqSession: RfqSession;
	@Input() rfqSessionProducts: RfqSessionProduct[];
	@Input() products: Product[];
	@Input() suppliers: Supplier[];
	@Input() otherSuppliers: Supplier[];
	@Input() defaultProducts: Product[];

	@Output() auctionUpdateCompleted: EventEmitter<RfqSession> = new EventEmitter();
	@Output() showAuctionSupplierBidsClicked: EventEmitter<string> = new EventEmitter();
	@Output() showAuctionSupplierCreateBidsClicked: EventEmitter<string> = new EventEmitter();
	@Output() showAuctionBidEditClicked: EventEmitter<RfqSessionBid> = new EventEmitter();
	@Output() showAuctionSupplierNoteEditClicked: EventEmitter<{
		supplierId: string;
		noteKey: string;
		noteKeyDisplay: string;
	}> = new EventEmitter();
	@Output() showAuctionExtendClicked: EventEmitter<boolean> = new EventEmitter();
	// eslint-disable-next-line max-len
	@Output() updateAuctionView: EventEmitter<'auction' | 'help' | 'supplierCreateBids' | 'supplierBids' | 'supplierNoteEdit' | 'extend'> =
		new EventEmitter();

	public rfpDataButtonDisabled = false;
	public rfpDataButtonText = 'Download RFP Information';

	public bidSessionDataButtonDisabled = false;
	public bidSessionDataButtonText = 'Bid Session';

	public isERCOT = false;
	public showMarketSuggestionSuccess = false;
	public choosingMarketSuggestion = false;

	public oldIsOnHold: boolean;
	public terms: number[];
	public greenPercentages: number[];

	constructor(
		private router: Router,
		private graphqlService: GraphqlService,
		private modalService: BsModalService,
		private toastrService: ToastrService,
		private notificationService: NotificationService,
		private datePipe: DatePipe,
		private helperService: HelperService,
		private fb: FormBuilder,
		private auctionService: AuctionService,
	) {
		super();
		this.submitText = 'Update Session';
		this.submitAudit = 'auction-in-progress:updateRfqSession';
	}

	get isEnterpiseAgent(): boolean {
		return this.loggedInUser?.agent && this.loggedInUser?.agent?.isEnterprise;
	}

	get productNote(): string {
		return this.auctionService.getProductNote(this.rfqSession.contract.stateId, this.form.products.control);
	}

	public yesNoTextMap = (option: boolean) => (option ? 'Yes' : 'No');
	public termTextMap = (option: number) => `${option} month${option === 1 ? '' : 's'}`;
	public greenPercentageTextMap = (option: number) => `${option}%`;

	public async loadPageData(): Promise<void> {
		this.oldIsOnHold = this.rfqSession.isOnHold;
		this.terms = this.helperService.generateNumberList(1, 120);
		this.greenPercentages = this.helperService.generateNumberList(0, 100, 10);
	}

	public getForm() {
		return this.fb.group(
			{
				margin: this.rfqSession.contract.margin,
				startDate: [this.rfqSession.startDate, [Validators.required]],
				startTime: [this.rfqSession.startTime, [Validators.required]],
				endDate: [this.rfqSession.endDate, [Validators.required]],
				endTime: [this.rfqSession.endTime, [Validators.required]],
				instructions: this.rfqSession.instructions,
				instructionsAdmin: this.rfqSession.instructionsAdmin,
				isCustomerInvited: this.rfqSession.isCustomerInvited,
				isOnHold: this.rfqSession.isOnHold,
				rfqSessionBids: this.fb.array(
					this.rfqSession.bids?.map(b =>
						this.fb.group({
							addDate: b.addDate,
							billingMethod: b.billingMethod,
							rfqSessionId: b.rfqSessionId,
							rate: b.rate,
							rfqSessionSupplierId: b.rfqSessionSupplierId,
						}),
					) ?? [],
				),
				products: this.fb.array(
					this.rfqSession.products?.map(p => this.getProductGroup(p, true)),
					[Validators.required],
				),
				suppliers: this.fb.array(this.getSupplierGroups(this.suppliers)),
				otherSuppliers: this.fb.array(this.getSupplierGroups(this.otherSuppliers)),
				optedOutSuppliers: this.fb.array(
					this.rfqSession.suppliers
						?.filter(s => s.isOptOut)
						.map(s =>
							this.fb.group({
								rfqSessionSupplierId: s.rfqSessionSupplierId,
								name: s.supplier.name,
								supplierId: s.supplierId,
								supplierSelected: false,
								hidden: false,
							}),
						) ?? [],
				),
				onHoldSuppliers: this.fb.array(
					this.rfqSession.suppliers
						?.filter(s => s.isOnHold)
						.map(s =>
							this.fb.group({
								rfqSessionSupplierId: s.rfqSessionSupplierId,
								name: s.supplier.name,
								supplierId: s.supplierId,
								supplierSelected: false,
								hidden: false,
							}),
						) ?? [],
				),
			},
			{ validators: productsValidator },
		);
	}

	private getSupplierGroups(suppliers: Supplier[]) {
		return suppliers.map(s =>
			this.fb.group({
				name: s.name,
				supplierId: s.supplierId,
				supplierSelected: false,
				hidden: !!this.rfqSession.suppliers.find(rs => rs.supplierId === s.supplierId),
			}),
		);
	}

	private getProductGroup(product: Subset<RfqSessionProduct>, hidden: boolean, defaultTerm?: number) {
		return this.fb.group<AuctionProduct>({
			rfqSessionProductId: new FormControl<string>(product.rfqSessionProductId),
			type: new FormControl<string>(product.productId, [Validators.required]),
			term: new FormControl<number>(product.term ?? defaultTerm ?? 12, [Validators.required]),
			greenPercentage: new FormControl<number>(product.greenPercentage ?? this.greenPercentages[0], [Validators.required]),
			hidden: new FormControl<boolean>(hidden),
		});
	}

	public addProduct() {
		const group = this.getProductGroup(this.defaultProducts[0], false);
		this.form.products.control.push(group);
	}

	public async onFormLoaded(): Promise<void> {
		// disable this session once invited
		if (this.rfqSession.isCustomerInvited) {
			this.form.isCustomerInvited.disable();
		}
	}

	protected async onFormSubmitted() {
		await this.updateRfqSession();
	}

	private async updateRfqSession(): Promise<void> {
		const isReleasingAuction = !StringUtils.toBoolean(this.form.isOnHold.value) && this.oldIsOnHold;
		const result =
			!isReleasingAuction ||
			(await this.notificationService.confirm({
				title: 'Update Session',
				body: 'You are about to notify the selected suppliers.  Do you want to continue?',
			}));
		if (result) {
			const rfqSessionResult = await this.graphqlService.updateRfqSession(this.rfqSession.rfqSessionId, this.rfqSessionDTO());

			// Create all new suppliers
			for (const supplier of [...this.form.suppliers, ...this.form.otherSuppliers]
				.filter(c => c.supplierSelected.value === true)
				.map(s => ({ supplierId: s.supplierId.value, rfqSessionId: this.rfqSession.rfqSessionId, pendingAddition: false }))) {
				await this.graphqlService.createRfqSessionSupplier(supplier);
			}

			// Opt back in any opted out suppliers checked
			for (const supplier of this.form.optedOutSuppliers.filter(c => c.supplierSelected.value === true)) {
				await this.graphqlService.updateRfqSessionSupplier(supplier.rfqSessionSupplierId.value, { isOptOut: false });
			}

			// Opt back in any opted out suppliers checked
			for (const supplier of this.form.onHoldSuppliers.filter(c => c.supplierSelected.value === true)) {
				await this.graphqlService.updateRfqSessionSupplier(supplier.rfqSessionSupplierId.value, { isOnHold: false });
			}

			// Create new products
			for (const product of this.form.products
				.filter(p => p.hidden.value === false)
				.map(p => ({
					productId: p.type.value,
					term: Number(p.term.value),
					greenPercentage: Number(p.greenPercentage.value),
					rfqSessionId: this.rfqSession.rfqSessionId,
				}))) {
				await this.graphqlService.createRfqSessionProduct(product);
			}

			this.toastrService.success('Auction Successully Updated!', 'Pricing Session');
			this.auctionUpdateCompleted.emit(new RfqSession(rfqSessionResult.data.updateRfqSession));
		}
	}

	private rfqSessionDTO() {
		const form = this.formGroup.value;
		return {
			startDate: this.datePipe.transform(form.startDate, 'yyyy-MM-ddT00:00:00.000'),
			startTime: this.datePipe.transform(form.startTime, 'yyyy-MM-ddTHH:mm:ss.SSS'),
			endDate: this.datePipe.transform(form.endDate, 'yyyy-MM-ddT00:00:00.000'),
			endTime: this.datePipe.transform(form.endTime, 'yyyy-MM-ddTHH:mm:ss.SSS'),
			margin: form.margin,
			isCustomerInvited: form.isCustomerInvited,
			instructions: form.instructions,
			instructionsAdmin: form.instructionsAdmin,
			agentId: this.rfqSession.contract.agentId,
			isOnHold: form.isOnHold,
		};
	}

	public getExtendSessionMsg(): string {
		return 'Extend Session - Limited to 2 per week';
	}

	get hasBestBid() {
		return this.rfqSession?.products?.some(p => !!p.bestBids);
	}

	get showOperationalView(): boolean {
		return this.loggedInUser.isAdmin || this.loggedInUser.isOwner || this.loggedInUser.isFinance || this.loggedInUser.isOperations;
	}

	get units() {
		return this.rfqSession?.contract.serviceTypeId === this.CONSTANTS.serviceTypes.naturalGas.serviceTypeId ? '$/DTH' : '$/MWh';
	}

	public showAuctionExtend(): void {
		this.showAuctionExtendClicked.next(true);
	}

	public showAuctionSupplierBids(rfqSessionSupplier: RfqSessionSupplier): void {
		this.showAuctionSupplierBidsClicked.next(rfqSessionSupplier.rfqSessionSupplierId);
	}

	public showAuctionBidEdit(bid: RfqSessionBid): void {
		this.showAuctionBidEditClicked.next(bid);
	}

	public showAuctionSupplierCreateBids(rfqSessionSupplier: RfqSessionSupplier): void {
		if (!this.rfqSession.products.length) {
			this.toastrService.warning('Please add at least one product to be able to submit a bid!');
			return;
		}

		this.showAuctionSupplierCreateBidsClicked.next(rfqSessionSupplier.rfqSessionSupplierId);
	}

	public showAuctionSupplierNoteEdit(rfqSessionSupplier: RfqSessionSupplier, noteKey: string, noteKeyDisplay: string): void {
		this.showAuctionSupplierNoteEditClicked.next({ supplierId: rfqSessionSupplier.supplierId, noteKey, noteKeyDisplay });
	}

	public showAuctionSendMessageModal(): void {
		const queryParams = `rfqSessionId=${this.rfqSession.rfqSessionId}` + `&supplierId=${this.rfqSession.suppliers.map(s => s.supplierId).join(',')}`;
		window.open(
			`${window.location.origin}/auction/sendMessage?${queryParams}`,
			'targetWindow',
			'toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=800,height=600',
		);
	}

	public showOptOutModal(rfqSessionSupplier: RfqSessionSupplier): void {
		const modalRef = this.modalService.show(AuctionSupplierOptOutModalComponent, {
			class: 'pk-modal details-modal',
			initialState: {
				rfqSessionSupplierId: rfqSessionSupplier.rfqSessionSupplierId,
				contractNum: this.rfqSession.contract.displayContractNum,
				dbaName: this.rfqSession.contract.customer.dbaName,
			},
		});
		modalRef.content.updated.subscribe(() => {
			rfqSessionSupplier.isOptOut = true;
		});
	}

	public async removeRfqSessionSupplier(rfqSessionSupplier: RfqSessionSupplier, index: number): Promise<void> {
		this.warnings = [];
		try {
			const dialogResult = await confirm('Are you sure you want to remove this supplier?\nThis action cannot be reversed!', 'Confirm');
			if (dialogResult) {
				rfqSessionSupplier['loading'] = true;
				await this.graphqlService.deleteRfqSessionSupplier(rfqSessionSupplier.rfqSessionSupplierId);
				this.toastrService.success('Successfully Removed Supplier', 'Contract Pricing Session');
				this.rfqSession.suppliers.splice(index, 1);
				this.form.suppliers.control.push(
					this.fb.group({
						name: rfqSessionSupplier.supplier.name,
						supplierId: rfqSessionSupplier.supplierId,
						supplierSelected: false as boolean,
						hidden: false as boolean,
					}),
				);
			}
		} catch (e) {
			this.toastrService.warning(
				`There was a problem removing the supplier. We have been ` +
				`notified and are working to fix the issue. Please check back again in 30 minutes.`,
				'Contract Pricing Session',
			);
		}
	}

	public async removeRfqSessionProduct(rfqSessionProduct: RfqSessionProduct): Promise<void> {
		this.warnings = [];
		try {
			const dialogResult = await confirm('Are you sure you want to remove this product?\nThis action cannot be reversed!', 'Confirm');
			if (dialogResult) {
				await this.graphqlService.deleteRfqSessionProduct(rfqSessionProduct.rfqSessionProductId);
				this.toastrService.success('Successfully Removed Product', 'Contract Pricing Session');
				this.rfqSession.products.splice(
					this.rfqSession.products.findIndex(p => p.rfqSessionProductId === rfqSessionProduct.rfqSessionProductId),
					1,
				);
				this.form.products.remove(this.form.products.find(p => p.rfqSessionProductId.value === rfqSessionProduct.rfqSessionProductId));
			}
		} catch (e) {
			this.toastrService.warning(
				`There was a problem removing the product. We have been ` +
				`notified and are working to fix the issue. Please check back again in 30 minutes.`,
				'Contract Pricing Session',
			);
		}
	}

	public async downloadRfpData(): Promise<void> {
		try {
			this.rfpDataButtonDisabled = true;
			this.rfpDataButtonText = 'Processing...';
			const res = await this.graphqlService.getRfqSessionInfo({
				contractId: this.rfqSession.contract.contractId,
				rfqSessionId: this.rfqSession.rfqSessionId,
			});
			if (res && res.data.rfqSessionInfo) {
				const link = document.createElement('a');
				const prefix = 'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,';
				link.href = prefix + res.data.rfqSessionInfo.response;
				link.download = 'RFP Information.xlsx';
				document.body.appendChild(link);
				link.click();
				link.remove();
			}
		} catch (e) {
			this.warnings = HelperService.getErrorMessage(e);
		}

		this.rfpDataButtonDisabled = false;
		this.rfpDataButtonText = 'Download RFP Information';
	}

	public async downloadBidSession(): Promise<void> {
		try {
			this.bidSessionDataButtonDisabled = true;
			this.bidSessionDataButtonText = 'Downloading...';
			const res = await this.graphqlService.getRfqSessionBidInfo({
				rfqSessionId: this.rfqSession.rfqSessionId,
			});
			if (res && res.data.rfqSessionBidInfo) {
				const link = document.createElement('a');
				const prefix = 'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,';
				link.href = prefix + res.data.rfqSessionBidInfo.response;
				link.download = `Bid Session.xlsx`;
				document.body.appendChild(link);
				link.click();
				link.remove();
			}
		} catch (e) {
			this.warnings = HelperService.getErrorMessage(e);
		}

		this.bidSessionDataButtonDisabled = false;
		this.bidSessionDataButtonText = 'Bid Session';
	}

	public async goCreateQuote(): Promise<void> {
		const url = this.router.serializeUrl(
			this.router.createUrlTree(['./rates/quote/', this.rfqSession.contract.contractId, 'rfqSession', this.rfqSession.rfqSessionId]),
		);
		window.open(url, '_blank');
	}

	public async onSelectedMarketSuggestion(marketSuggestion: RfqSessionMarketSuggestion): Promise<void> {
		try {
			this.choosingMarketSuggestion = true;
			await this.graphqlService.chooseMarketSuggestion(this.rfqSession.rfqSessionId, {
				effectiveDate: moment(marketSuggestion.effectiveDate).format('YYYY-MM-DDT00:00:00'),
			});
			if (marketSuggestion.percentChange !== 0) {
				this.showMarketSuggestionSuccess = true;
			}
			this.rfqSession.marketSuggestionEffectiveDate = marketSuggestion.effectiveDate;
			this.rfqSession.contract.effectiveDate = marketSuggestion.effectiveDate;
		} catch (e) {
			this.toastrService.warning(
				`There was a problem choose the market suggestion, We have been ` +
				`notified and are working to fix the issue. Please check back again in 30 minutes.`,
				'Contract Pricing Session',
			);
		}
		this.choosingMarketSuggestion = false;
	}
}
